// src/antdviews/components/account.detail/RecordListTab.tsx

import axios from 'axios'
import React from 'react'
import {
    Card,
    Table,
    Button,
    Space,
    Tooltip,
    Row,
    Col,
    Typography,
    Select,
    Pagination,
} from 'antd'
import {
    CloseCircleOutlined,
    FileTextOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { DataApi, FilePath, ROOT, URL, UserMgmtApi } from '../../../services/Api'
import { NEW_RECORD_SYSTEM, ROLE } from '../../../services/Macro'
import { componentUtil } from '../../../utils/ComponentUtil'
import { encrypUtil } from '../../../utils/EncrypUtil'
import { mathUtil } from '../../../utils/MathUtil'

const { Text, Title } = Typography

interface PhoneInfo {
    phoneMode: string
    phoneManufacturer: string
    phoneVersion: string
    phoneSDK: string
    hasPhoneMode: boolean
    hasPhoneManufacturer: boolean
    hasPhoneVersion: boolean
    hasPhoneSDK: boolean
}

interface UserItem {
    user_id: string
    name: string
    middleName?: string
    lastName?: string
    gender: number
    genderStr: string
    age: number
    birthday: string
    created: string
    online_portrait?: string
    yearBirth?: number
    monthBirth?: number
    dayBirth?: number
}

interface RecordItem {
    url: string
    fileName: string
    nickname: string
    device: string
    size: string
    startTime: string
    timeLength: string
    sourceStr: string
    description?: string
    href: string
    reports: Array<{ name: string; type: number }>
    decodeProgress: number
    rate: number
    meta: any
    location: number
    source: number
    reportTypes: number
}

interface LogItem {
    id: string
    date: string
    size: string
    updateTime: string
    source: number
    filePath: string
    fileName: string
    uuid: string
    rowspan: number
    hideDate?: boolean
}

interface DetailData {
    isActive: boolean
    accountName: string
    accountEmail: string
    onlinePortrait: string | null
    users: { list: UserItem[]; totalPages?: number; currentPage?: number; totalRecords?: number; pages?: number[] }
    logs: { list: LogItem[]; totalPages?: number; currentPage?: number; totalRecords?: number; pages?: number[] }
    records: { list: RecordItem[]; totalPages?: number; currentPage?: number; totalRecords?: number; pages?: number[] }
    phoneInfo: PhoneInfo
}

interface RecordListTabProps {
    detail: DetailData
    trans: (key: string) => string
    handleConfirm: (title: string, info: string, op: Function) => void
    handleError: (title: string, info: string) => void
    notify: (msg: string) => void
    changeRecordList: (size: number, page: number) => void
}

interface RecordListTabState {
    recordsPerPage: number
    recordsCurrentPage: number
    decodeProgress: Record<string, number>
}

export default class RecordListTab extends React.Component<RecordListTabProps, RecordListTabState> {
    private decodeItemSequence: RecordItem[] = []

    constructor(props: RecordListTabProps) {
        super(props)
        this.state = {
            recordsPerPage: 10,
            recordsCurrentPage: 1,
            decodeProgress: {}
        }
    }

    componentDidMount() {
        const progressMap: Record<string, number> = {}
        for (let i = 0; i < this.props.detail.records.list.length; i++) {
            const item = this.props.detail.records.list[i]
            const progress = Math.max(item.decodeProgress, 0)
            progressMap[item.url] = progress
        }
        this.setState({ decodeProgress: progressMap })
    }

    /** RPP: Records Per Page */
    onRppChange = (event: any) => {
        const oldRecordsPerPage = this.state.recordsPerPage
        const newRecordsPerPage = parseInt(event.key)
        const newRecordsCurrentPage = mathUtil.getNewPage(oldRecordsPerPage, this.state.recordsCurrentPage, newRecordsPerPage)

        this.setState({
            recordsPerPage: newRecordsPerPage,
            recordsCurrentPage: newRecordsCurrentPage
        }, () => {
            this.updateRecordList()
        })
    }

    updateRecordList = () => {
        this.props.changeRecordList(this.state.recordsPerPage, this.state.recordsCurrentPage)
    }

    prevRecordPage = () => {
        if (this.state.recordsCurrentPage > 1) {
            this.setState({
                recordsCurrentPage: this.state.recordsCurrentPage - 1
            }, () => {
                this.updateRecordList()
            })
        }
    }

    nextRecordPage = () => {
        const totalPages = this.props.detail.records.totalPages || 0
        if (this.state.recordsCurrentPage < totalPages) {
            this.setState({
                recordsCurrentPage: this.state.recordsCurrentPage + 1
            }, () => {
                this.updateRecordList()
            })
        }
    }

    firstRecordPage = () => {
        if (this.state.recordsCurrentPage > 1) {
            this.setState({
                recordsCurrentPage: 1
            }, () => {
                this.updateRecordList()
            })
        }
    }

    lastRecordPage = () => {
        const totalPages = this.props.detail.records.totalPages || 0
        if (this.state.recordsCurrentPage < totalPages) {
            this.setState({
                recordsCurrentPage: totalPages
            }, () => {
                this.updateRecordList()
            })
        }
    }

    onRecordPageChange = (event: any) => {
        this.setState({
            recordsCurrentPage: parseInt(event.key)
        }, () => {
            this.updateRecordList()
        })
    }

    /** 获取账号信息 */
    getAccountInfo = (): [string | null, string, string] => {
        const token = localStorage.getItem('token') || null
        const lang = localStorage.getItem('lang') || 'zh_CN'
        const role = localStorage.getItem('role') || String(ROLE.SUPER)
        return [token, lang, role]
    }

    isItemInDecodeSequence = (item: RecordItem): boolean => {
        for (let i = 0; i < this.decodeItemSequence.length; i++) {
            const itemInSequence = this.decodeItemSequence[i]
            if (item.url === itemInSequence.url) {
                return true
            }
        }
        return false
    }

    decodeEcg = async (item: RecordItem) => {
        const url = item.url
        if (!this.isItemInDecodeSequence(item)) {
            this.decodeItemSequence.push(item)
        }
        if (this.decodeItemSequence.length > 0 && this.decodeItemSequence[0].url === url) {
            const searchParams = new URLSearchParams(window.location.search)
            const uuid = searchParams.get('uuid')
            const [token, lang, role] = this.getAccountInfo()
            const requestData = {
                uuid: uuid,
                role: role,
                token: token,
                lang: lang.split('_')[0],
                url: url
            }
            const response = await axios.post(DataApi.DECODE_ECG, requestData)

            if (response.data && response.data.data) {
                item.decodeProgress = response.data.data.progress
                const newProgress = { ...this.state.decodeProgress }
                newProgress[item.url] = item.decodeProgress
                this.setState({ decodeProgress: newProgress })
                if (item.decodeProgress < 10000) {
                    setTimeout(() => {
                        this.decodeEcg(item)
                    }, 100)
                } else {
                    this.decodeItemSequence.shift()
                    if (this.decodeItemSequence.length > 0) {
                        this.decodeEcg(this.decodeItemSequence[0])
                    }
                }
            }
        }
    }

    delCsv = async (item: RecordItem) => {
        const t = this.props.trans
        const url = item.url
        const fileName = item.fileName
        const title = t('plsConfirm')
        const info = `${t('delete')} CSV ${fileName} ?`
        const op = async () => {
            const searchParams = new URLSearchParams(window.location.search)
            const uuid = searchParams.get('uuid')
            const [token, lang, role] = this.getAccountInfo()
            const requestData = {
                uuid: uuid,
                role: role,
                token: token,
                lang: lang.split('_')[0],
                url: url
            }
            await axios.post(DataApi.DEL_CSV, requestData)
            item.decodeProgress = -1
            const newProgress = { ...this.state.decodeProgress }
            newProgress[item.url] = -1
            this.setState({ decodeProgress: newProgress })
        }
        this.props.handleConfirm(title, info, op)
    }

    deleteRecord = (event: any) => {
        const t = this.props.trans
        const searchParams = new URLSearchParams(window.location.search)
        const uuid = searchParams.get('uuid')
        const url = event.currentTarget.getAttribute('data-url')
        const filename = event.currentTarget.getAttribute('data-filename')
        const title = t('plsConfirm')
        const info = `${t('confirmToDelRecord')} ${filename} ?`
        const op = async () => {
            const [token, lang, role] = this.getAccountInfo()
            const requestData: any = {
                uuid: uuid,
                role: role,
                token: token,
                lang: lang.split('_')[0],
                urls: [url]
            }
            if (NEW_RECORD_SYSTEM) requestData.newRecord = true
            const params = await encrypUtil.encodeRequestRsa(requestData)
            const response = await axios.post(UserMgmtApi.SUPER_DEL_RECORD, params)
            if (400 === response.data.code) {
                this.props.handleError(t('serverError'), response.data.message)
            }
            this.props.notify(t('delSuccess'))
            this.updateRecordList()
        }
        this.props.handleConfirm(title, info, op)
    }

    showRecordDetail = (index: number, target: any) => {
        const t = this.props.trans
        const item = this.props.detail.records.list[index]
        let trValueReport = ''
        if (item.reports) {
            for (let i = 0; i < item.reports.length; i++) {
                const report = item.reports[i]
                trValueReport += `<a href="${FilePath.REPORTS}${item.url}_${report.type}.pdf" target="_blank">${t(report.name)}</a>&nbsp;|&nbsp;`
            }
        }
        if (NEW_RECORD_SYSTEM) {
            trValueReport += `<a href="${ROOT}/ecgShow?newRecord=1&url=${item.url}">ECG</a>&nbsp;|&nbsp;`
            trValueReport += `<a href="${ROOT}/hrvShow?newRecord=1&url=${item.url}">HRV</a>`
        } else {
            trValueReport += `<a href="${ROOT}/ecgShow?url=${item.url}">ECG</a>&nbsp;|&nbsp;`
            trValueReport += `<a href="${ROOT}/hrvShow?url=${item.url}">HRV</a>`
        }
        const progress = Math.max(item.decodeProgress, 0)
        const currentProgress = this.state.decodeProgress[item.url] === undefined ? progress : this.state.decodeProgress[item.url]
        if (currentProgress === 10000) {
            trValueReport += `&nbsp;|&nbsp;<a href="${URL}/files/ecg/${item.url}.csv" download="${item.fileName}.csv">CSV</a>`
        }
        const attrList = [
            { trName: 'trReport', trTitle: t('report'), trValue: trValueReport },
            {
                trName: 'trDownloadRecord', trTitle: t('download'),
                trValue: `<a href="${URL}/download.do?apikey=1F192D3A136F4DAF9B6A4E3E07893E2D&url=${item.url}" download="${item.fileName}">${item.fileName.slice('HMApp_Record_'.length)}</a>`
            },
            { trName: 'trSize', trTitle: t('size'), trValue: `${item.size}` },
            { trName: 'trDevice', trTitle: t('device'), trValue: `${item.device}` },
        ]
        componentUtil.showDetail(6, index, target, this.props.detail.records.list, attrList)
    }

    getReportItems(item: RecordItem) {
        const t = this.props.trans
        const progress = Math.max(item.decodeProgress, 0)
        const currentProgress = this.state.decodeProgress[item.url] === undefined ? progress : this.state.decodeProgress[item.url]
        const ecgShowPath = NEW_RECORD_SYSTEM ? `/ecgShow?newRecord=1&url=` : `/ecgShow?url=`
        const hrvShowPath = NEW_RECORD_SYSTEM ? `/hrvShow?newRecord=1&url=` : `/hrvShow?url=`

        const reportLinks = item.reports.map((report, idx) => (
            <a
                key={idx}
                href={`${FilePath.REPORTS}${item.url}_${report.type}.pdf`}
                target="_blank"
                rel="noopener noreferrer"
            >
                {t(report.name)}
            </a>
        ))

        if (currentProgress === 10000) {
            return (
                <Space size="small" wrap>
                    {reportLinks}
                    <a href={`${ROOT}${ecgShowPath}${item.url}`} target="_blank" rel="noopener noreferrer">ECG</a>
                    <a href={`${ROOT}${hrvShowPath}${item.url}`} target="_blank" rel="noopener noreferrer">HRV</a>
                    {this.getDecodeStatus(item)}
                    <CloseCircleOutlined
                        style={{ color: '#ff0000', cursor: 'pointer' }}
                        onClick={(e) => { this.delCsv(item) }}
                    />
                </Space>
            )
        }
        return (
            <Space size="small" wrap>
                {reportLinks}
                <a href={`${ROOT}${ecgShowPath}${item.url}`} target="_blank" rel="noopener noreferrer">ECG</a>
                <a href={`${ROOT}${hrvShowPath}${item.url}`} target="_blank" rel="noopener noreferrer">HRV</a>
                {this.getDecodeStatus(item)}
            </Space>
        )
    }

    getDecodeOp(item: RecordItem) {
        const t = this.props.trans
        const progress = Math.max(item.decodeProgress, 0)
        const currentProgress = this.state.decodeProgress[item.url] === undefined ? progress : this.state.decodeProgress[item.url]

        if (currentProgress === 10000) {
            return (
                <Tooltip title={t('deleteCSV')}>
                    <CloseCircleOutlined
                        style={{ color: '#ff0000', cursor: 'pointer' }}
                        onClick={(e) => { this.delCsv(item) }}
                    />
                </Tooltip>
            )
        }
        return (
            <Button
                size="small"
                onClick={(e) => { this.decodeEcg(item) }}
            >
                {Math.round(currentProgress / 100)}%
            </Button>
        )
    }

    getDecodeStatus(item: RecordItem) {
        const t = this.props.trans
        const progress = Math.max(item.decodeProgress, 0)
        const decodeProgress = this.state.decodeProgress[item.url] === undefined ? progress : this.state.decodeProgress[item.url]

        if (decodeProgress === 10000) {
            return (
                <a href={`${FilePath.CSV}${item.url}.csv`} download={`${item.fileName}.csv`}>
                    CSV
                </a>
            )
        }
        return (
            <Button size="small" onClick={(e) => { this.decodeEcg(item) }}>
                {t('decode')}({decodeProgress / 100}%)
            </Button>
        )
    }

    render() {
        const t = this.props.trans

        const recordCurP = this.props.detail.records.currentPage || 1
        const recordTotalP = this.props.detail.records.totalPages || 0
        const recordTotalR = this.props.detail.records.totalRecords || 0
        const recordList = this.props.detail.records.list || []


        // 计算表格滚动高度
        const tableScrollY = window.innerHeight - 320
        const rowHeight = 56
        const headerHeight = 38
        const availableHeight = tableScrollY - headerHeight
        const totalContentHeight = recordList.length * rowHeight
        const needScroll = totalContentHeight > availableHeight

        const recordColumns: ColumnsType<RecordItem> = [
            {
                title: t('user'),
                dataIndex: 'nickname',
                key: 'nickname',
                width: 90,
                ellipsis: true,
            },
            {
                title: t('device'),
                dataIndex: 'device',
                key: 'device',
                width: 90,
                ellipsis: true,
            },
            {
                title: t('size'),
                dataIndex: 'size',
                key: 'size',
                width: 60,
                ellipsis: true,
            },
            {
                title: t('startTime'),
                dataIndex: 'startTime',
                key: 'startTime',
                width: 120,
                ellipsis: true,
            },
            {
                title: t('timeLength'),
                dataIndex: 'timeLength',
                key: 'timeLength',
                width: 60,
                ellipsis: true,
            },
            {
                title: t('source'),
                dataIndex: 'sourceStr',
                key: 'sourceStr',
                width: 50,
                ellipsis: true,
            },
            {
                title: t('download'),
                key: 'download',
                width: 300,
                ellipsis: true,
                render: (_, record) => (
                    <Tooltip title={record.fileName} placement="topLeft">
                        <a
                            href={record.href}
                            download={record.fileName}
                            style={{
                                display: 'inline-block',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                verticalAlign: 'bottom',
                            }}
                        >
                            {record.fileName}
                        </a>
                    </Tooltip>
                ),
            },
            {
                title: t('report'),
                key: 'report',
                width: 120,
                ellipsis: true,
                render: (_, record) => this.getReportItems(record),
            },
            {
                title: t('operation'),
                key: 'operation',
                width: 60,
                render: (_, record) => (
                    <Tooltip title={t('delete')}>
                        <CloseCircleOutlined
                            style={{ color: '#ff0000', cursor: 'pointer' }}
                            onClick={this.deleteRecord}
                            data-url={record.url}
                            data-filename={record.fileName}
                        />
                    </Tooltip>
                ),
            },
        ]

        return (
            <Card
                title={
                    <Space>
                        <FileTextOutlined style={{ color: '#1890ff' }} />
                        <Title level={4} style={{ margin: 0 }}>
                            {t('recordList')}
                        </Title>
                    </Space>
                }
                style={{
                    borderRadius: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    background: 'var(--card-bg)',
                }}
                bodyStyle={{
                    padding: '6px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                }}
            >
                {/* 表格区域 - 撑满剩余空间 */}
                <div style={{ flex: 1, minHeight: 0 }}>
                    <Table
                        dataSource={recordList}
                        rowKey={(record) => record.url}
                        pagination={false}
                        size="small"
                        columns={recordColumns}
                        bordered
                        scroll={{
                            y: needScroll ? (tableScrollY > 200 ? tableScrollY : 300) : undefined,
                        }}
                        style={{ borderRadius: 6, height: '100%' }}
                        tableLayout="fixed"
                    />
                </div>

                {/* 分页区域 - 固定在底部 */}
                <Row
                    justify="space-between"
                    align="middle"
                    style={{
                        marginTop: 16,
                        flexShrink: 0,
                        paddingTop: 8,
                        borderTop: '1px solid #f0f0f0',
                    }}
                >
                    <Col>
                        <Text type="secondary">
                            {t('table.footer.totalRecords') || '共'} {recordTotalR} {t('table.footer.records') || '条记录'}
                            ，{t('table.footer.currentPage') || '当前第'} {recordCurP}/{recordTotalP} {t('table.footer.page') || ''}
                        </Text>
                    </Col>
                    <Col>
                        <Space>
                            <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                                {t('table.footer.pageSize') || '每页'}
                            </span>
                            <Select
                                value={this.state.recordsPerPage}
                                onChange={(value: number) => {
                                    const newRecordsCurrentPage = mathUtil.getNewPage(
                                        this.state.recordsPerPage,
                                        this.state.recordsCurrentPage,
                                        value
                                    )
                                    this.setState({
                                        recordsPerPage: value,
                                        recordsCurrentPage: newRecordsCurrentPage
                                    }, () => {
                                        this.updateRecordList()
                                    })
                                }}
                                style={{ width: 80 }}
                            >
                                <Select.Option value={10}>10</Select.Option>
                                <Select.Option value={20}>20</Select.Option>
                                <Select.Option value={50}>50</Select.Option>
                                <Select.Option value={100}>100</Select.Option>
                            </Select>
                            <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                                {t('table.footer.items') || '条'}
                            </span>
                            <Pagination
                                current={this.state.recordsCurrentPage}
                                total={recordTotalR}
                                pageSize={this.state.recordsPerPage}
                                onChange={(page) => {
                                    this.setState({
                                        recordsCurrentPage: page
                                    }, () => {
                                        this.updateRecordList()
                                    })
                                }}
                                showSizeChanger={false}
                                showQuickJumper
                                locale={{
                                    items_per_page: t('table.footer.itemsPerPage') || '条/页',
                                    jump_to: t('table.footer.jumpTo') || '跳至',
                                    jump_to_confirm: t('table.footer.confirm') || '确定',
                                    page: t('table.footer.page') || '',
                                    prev_page: t('table.footer.prevPage') || '上一页',
                                    next_page: t('table.footer.nextPage') || '下一页',
                                    prev_5: t('table.footer.prev5') || '向前 5 页',
                                    next_5: t('table.footer.next5') || '向后 5 页',
                                    prev_3: t('table.footer.prev3') || '向前 3 页',
                                    next_3: t('table.footer.next3') || '向后 3 页',
                                }}
                            />
                        </Space>
                    </Col>
                </Row>
            </Card>
        )
    }
}